import type { NotFoundReason, SwitchboardSubject } from '@schemas/error-pages'
import { NOT_FOUND_HEADLINE_BY_REASON, NotFoundReasonEnum } from '@schemas/error-pages'
import { m } from 'motion/react'
import { fadeSlideUp } from '../../lib/motion.ts'
import { Badge } from '../badge.tsx'
import { Card, CardBody } from '../card.tsx'

const dateFormatter = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' })

// `ended` and `endAt` are independent fields (a target can be known-ended without a precise end
// date) — this renders a single consistent label from both rather than letting a range fall back
// to "ongoing" right next to an "ended" badge.
function endLabel(target: SwitchboardSubject['target']): string {
  if (target.endAt) return dateFormatter.format(target.endAt)
  return target.ended ? 'ended' : 'ongoing'
}

// RAL-162 board 1c: the object exists and is theirs, they're just in the wrong context. Amber,
// not red — nothing is broken. `subject.target.reachable` was already verified server-side by
// the caller before this rendered (RAL-163): an unreachable target is `access_revoked`, never a
// switch offered here that would fail.
export function SwitchboardLayout({ reason, subject }: { reason: NotFoundReason; subject: SwitchboardSubject }) {
  const { target } = subject
  const isAccountMismatch = reason === NotFoundReasonEnum.enum.wrong_account
  const contextNoun = isAccountMismatch ? 'account' : 'campaign'
  const range = `${dateFormatter.format(target.startAt)} – ${endLabel(target)}`

  return (
    <m.div variants={fadeSlideUp} initial="hidden" animate="visible" className="mx-auto max-w-xl">
      <Card className="border-signal-waiting-border">
        <CardBody className="flex flex-col gap-5 px-8 py-9">
          <div className="flex flex-wrap items-center gap-2.5">
            <Badge variant="warning">Wrong {contextNoun}</Badge>
            <span className="font-mono text-[10px] text-ink-secondary">{subject.path}</span>
          </div>

          <h1 className="font-display text-3xl font-extrabold uppercase leading-[1.05] tracking-tight text-ink-primary">
            {NOT_FOUND_HEADLINE_BY_REASON[reason]}
          </h1>

          <p className="max-w-prose text-sm leading-relaxed text-ink-soft">
            You're in <strong className="font-semibold text-ink-primary">{subject.currentCampaignName}</strong>. This
            one belongs to{' '}
            <strong className="font-semibold text-ink-primary">
              {isAccountMismatch ? `@${target.handle}` : target.name}
            </strong>
            {target.ended ? `, which ended${target.endAt ? ` ${dateFormatter.format(target.endAt)}` : ''}` : ''} —
            everything in it is still readable.
          </p>

          <div className="border border-border-hairline bg-ground-inset">
            <p className="border-b border-border-hairline bg-ground-recessed px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-ink-secondary">
              Switch and continue
            </p>
            <a
              href={target.queueHref}
              className="flex w-full items-center gap-3 border-b border-border-divider px-4 py-3.5 text-left transition-colors hover:bg-ground-hover"
            >
              <span className="min-w-0 flex-1">
                <span className="block font-display text-sm font-semibold uppercase tracking-[0.01em] text-ink-primary">
                  {target.name}
                </span>
                <span className="mt-1.5 block font-mono text-[10px] text-ink-secondary">
                  {range} · {target.ended ? 'ended' : 'active'} · @{target.handle}
                </span>
              </span>
              <span className="shrink-0 font-mono text-[10px] font-medium uppercase tracking-[0.11em] text-signal-oxide">
                Take me there
              </span>
            </a>
            <p className="px-4 py-3 font-mono text-[11px] text-ink-secondary">
              Switching moves you to that {contextNoun}'s queue.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-5 border-t border-border-hairline pt-4">
            <a
              href="/"
              className="font-mono text-[10px] font-medium uppercase tracking-[0.11em] text-signal-oxide hover:text-signal-oxide-bright"
            >
              Stay in {subject.currentCampaignName}
            </a>
            <span className="font-mono text-[11px] text-ink-secondary">
              Nothing was published or cancelled by landing here.
            </span>
          </div>
        </CardBody>
      </Card>
    </m.div>
  )
}
